import { inject, Injectable } from '@angular/core';
import { WINDOW } from '@constants/global-objects.constants';
import { Observable } from 'rxjs';

export type Platform =
  | 'ios'
  | 'ipad'
  | 'iphone'
  | 'android'
  | 'phablet'
  | 'tablet'
  | 'cordova'
  | 'capacitor'
  | 'electron'
  | 'pwa'
  | 'mobile'
  | 'mobileweb'
  | 'desktop'
  | 'hybrid';

@Injectable({
  providedIn: 'root',
})
export class PlatformService {
  private window = inject(WINDOW);

  constructor() {}

  public height(): number {
    return this.window.innerHeight;
  }

  public width(): number {
    return this.window.innerWidth;
  }

  public resize(): Observable<Event> {
    return new Observable((observer) => {
      this.window.addEventListener('resize', (event) => {
        observer.next(event);
      });
    });
  }

  public isMobile(): boolean {
    return this.matchMedia('(any-pointer:coarse)');
  }

  public isDesktop(): boolean {
    return !this.isMobile();
  }

  public isIpad(): boolean {
    // iOS 12 and below
    if (this.testUserAgent(/iPad/i)) {
      return true;
    }

    // iOS 13+
    if (this.testUserAgent(/Macintosh/i) && this.isMobile()) {
      return true;
    }

    return false;
  }

  public isIphone(): boolean {
    return this.testUserAgent(/iPhone/i);
  }

  public isIos(): boolean {
    return this.isIpad() || this.isIphone();
  }

  public isAndroid(): boolean {
    return this.testUserAgent(/android|sink/i);
  }

  public isAndroidTablet(): boolean {
    return this.isAndroid() && !this.testUserAgent(/mobile/i);
  }

  public isPhablet(): boolean {
    const { smallestScreen, largestScreen } =
      this.getSmallestAndLargestScreen();
    return (
      smallestScreen > 390 &&
      smallestScreen < 520 &&
      largestScreen > 620 &&
      largestScreen < 800
    );
  }

  public isTablet(): boolean {
    const { smallestScreen, largestScreen } =
      this.getSmallestAndLargestScreen();
    return (
      this.isIpad() ||
      this.isAndroidTablet() ||
      (smallestScreen > 460 &&
        smallestScreen < 820 &&
        largestScreen > 780 &&
        largestScreen < 1400)
    );
  }

  public platforms(): string[] {
    let platforms: Platform[] = ['desktop'];

    if (this.isMobile()) {
      platforms = ['mobile', 'mobileweb'];

      if (this.isIos()) {
        if (this.isIpad()) {
          platforms = ['ipad', 'ios', 'tablet', ...platforms];
        }

        if (this.isIphone()) {
          platforms = ['iphone', 'ios', ...platforms];
        }

        return platforms;
      } else if (this.isAndroid()) {
        if (this.isPhablet()) {
          platforms = ['phablet', ...platforms];
        }
        if (this.isTablet()) {
          platforms = ['tablet', ...platforms];
        }

        platforms = ['android', ...platforms];
        return platforms;
      }

      return platforms;
    }

    return platforms;
  }

  public testUserAgent(expr: RegExp): boolean {
    return expr.test(this.window.navigator.userAgent);
  }

  private matchMedia(query: string): boolean {
    return this.window.matchMedia?.(query).matches;
  }

  private getSmallestAndLargestScreen(): {
    smallestScreen: number;
    largestScreen: number;
  } {
    const width = this.width();
    const height = this.height();
    const smallestScreen = Math.min(width, height);
    const largestScreen = Math.max(width, height);
    return { smallestScreen, largestScreen };
  }
}
